import { createContext, ReactNode, useContext, useMemo } from "react";
import { toast } from "sonner";
import { useMessageDirectoryProfiles } from "@/hooks/use-supabase-queries";
import { useCalls } from "@/hooks/useCalls";
import { CallExperience } from "@/components/messages/CallExperience";

type CallController = ReturnType<typeof useCalls>;

const CallContext = createContext<CallController | null>(null);

export function CallProvider({ children }: { children: ReactNode }) {
  const { data: profiles } = useMessageDirectoryProfiles();

  const callableParticipants = useMemo(
    () =>
      (profiles || []).map((profile) => ({
        id: profile.id,
        name: `${profile.first_name} ${profile.last_name}`.trim() || "PropTRUST user",
        role: profile.role || "client",
      })),
    [profiles],
  );

  const calls = useCalls(callableParticipants);

  return (
    <CallContext.Provider value={calls}>
      {children}
      <CallExperience
        incomingCall={calls.incomingCall}
        activeCall={calls.activeCall}
        localStream={calls.localStream}
        remoteStream={calls.remoteStream}
        isMuted={calls.isMuted}
        isCameraEnabled={calls.isCameraEnabled}
        onAccept={() => void calls.acceptIncomingCall().catch((error) => {
          toast.error("Could not answer call", {
            description: calls.getCallErrorMessage(error),
          });
        })}
        onDecline={() => void calls.declineIncomingCall().catch((error) => {
          toast.error("Could not decline call", {
            description: calls.getCallErrorMessage(error),
          });
        })}
        onEnd={() => void calls.endActiveCall()}
        onToggleMute={calls.toggleMute}
        onToggleCamera={calls.toggleCamera}
      />
    </CallContext.Provider>
  );
}

export function useCallController() {
  const value = useContext(CallContext);
  if (!value) {
    throw new Error("useCallController must be used within a CallProvider.");
  }

  return value;
}
